import React, { useState } from "react";
import { Modal, Button, Switch, List, Card, Tooltip } from "antd";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { FaCog, FaGripVertical } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import useDashboardPreferencesStore from "../store/useDashboardPreferencesStore";

const DashboardSettings: React.FC = () => {
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);

  const { widgets, setWidgetVisibility, reorderWidgets, resetToDefaults } =
    useDashboardPreferencesStore();

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(widgets);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order property
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index,
    }));

    reorderWidgets(updatedItems);
  };

  return (
    <>
      <Tooltip title={t("Dashboard Settings")}>
        <Button
          className="icon-button-round__settings"
          size="middle"
          shape="circle"
          icon={<FaCog />}
          onClick={showModal}
        />
      </Tooltip>
      <Modal
        title={t("Dashboard Settings")}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="reset" onClick={resetToDefaults}>
            {t("Reset to default")}
          </Button>,
          <Button key="close" type="primary" onClick={handleCancel}>
            {t("Close")}
          </Button>,
        ]}
        width={600}
      >
        <p>
          {t(
            "Customize your dashboard by toggling widgets on/off and reordering them by drag and drop.",
          )}
        </p>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="widgets">
            {provided => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                <List
                  dataSource={widgets.sort((a, b) => a.order - b.order)}
                  renderItem={(widget, index) => (
                    <Draggable key={widget.id} draggableId={widget.id} index={index}>
                      {provided => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <Card className="widget-item" style={{ marginBottom: 8 }} size="small">
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center" }}>
                                <FaGripVertical style={{ marginRight: 12, color: "#999" }} />
                                <span>{t(widget.title)}</span>
                              </div>
                              <Switch
                                size="small"
                                checked={widget.visible}
                                onChange={checked => setWidgetVisibility(widget.id, checked)}
                              />
                            </div>
                          </Card>
                        </div>
                      )}
                    </Draggable>
                  )}
                />
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </Modal>
    </>
  );
};

export default DashboardSettings;
